import { AppAlertProps } from "components/v2/atoms/app-alert/AppAlert";
import useAppAlert, { UseAppAlertState } from "components/v2/atoms/app-alert/AppAlert.hooks";
import { defaultProps } from "helpers/common";
import { useState } from "react";
import { AlertCardProps } from "./AlertCard";
import { AppCardAlertProps } from "./AppCardAlert";

export interface ShowAlertOptions extends AlertCardProps {
    autoHideDuration?: number;
}

export interface UseAppCardAlertActions {
    showAlert: (options: ShowAlertOptions) => void;
    hideAlert: () => void;
}

export interface UseAppCardAlertResult {
    state: UseAppAlertState;
    actions: UseAppCardAlertActions;
    appCardAlertProps: AppCardAlertProps;
}

export interface UseAppCardAlertProps {
    showCloseButton?: boolean;
    defaultAutoHideDuration?: number;
    appAlertProps?: Partial<AppAlertProps>;
}

export function useAppCardAlert(props?: UseAppCardAlertProps): UseAppCardAlertResult {
    const appAlert = useAppAlert();
    const [alertCardProps, setAlertCardProps] = useState<AlertCardProps>();
    const [currentAutoHideDuration, setCurrentAutoHideDuration] = useState<number | undefined>(
        props?.defaultAutoHideDuration ?? 5000,
    );

    const actions: UseAppCardAlertActions = {
        ...appAlert.actions,
        showAlert(options: ShowAlertOptions) {
            const { autoHideDuration, ...alertProps } = options;
            setCurrentAutoHideDuration(autoHideDuration);
            setAlertCardProps(alertProps);
            appAlert.actions.showAlert();
        },
        hideAlert() {
            appAlert.actions.hideAlert();
        },
    };

    const appAlertProps = defaultProps(appAlert.appAlertProps, {
        autoHideDuration: currentAutoHideDuration,
        TransitionProps: {
            onExited() {
                setAlertCardProps(undefined);
                setCurrentAutoHideDuration(props?.defaultAutoHideDuration ?? 5000);
            },
        },
    });

    const appCardAlertProps: AppCardAlertProps = {
        appAlertProps: {
            ...appAlertProps,
            ...props?.appAlertProps,
        },
        alertCardProps,
        showCloseButton: props?.showCloseButton,
    };

    return {
        state: appAlert.state,
        actions,
        appCardAlertProps,
    };
}

export default useAppCardAlert;
